import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaMoneyBillWave, FaWallet } from "react-icons/fa6";
import { fetchData } from "../helpers/fetchData";
import FilterComponent from "./home/FilterComponent";
import { FaShoppingCart, FaUniversity } from "react-icons/fa";
import moment from "moment";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

const MetricCard = ({ title, value, icon, subTitle, currency }) => (
  <div className="bg-white rounded-md border border-gray-200 shadow-sm p-4 h-full">
    <div className="flex items-center gap-3">
      <div className="bg-gray-100 w-[40px] h-[40px] flex items-center justify-center rounded-full text-[#0070C2] text-xl">
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500 font-medium">{title}</p>
        <h3 className="text-xl font-bold text-gray-800">
          {value} {currency}
        </h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
  </div>
);
const ChartCard = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-md shadow-sm  h-[350px]">
    <div className="p-4 border-b border-gray-100">
      <h3 className="text-lg font-semibold text-gray-700">{title}</h3>
    </div>
    <div className="w-full h-[280px] pt-4 pr-4">{children}</div>
  </div>
);

export default function Home() {
  const [isSidebarVisible, setSidebarVisible] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(false);
  const [filterDate, setFilterDate] = useState({
    start_date: moment().subtract(1, "month").format("YYYY-MM-DD"),
    end_date: moment().format("YYYY-MM-DD"),
  });
  const safeGraphData = Array.isArray(graphData) ? graphData : [];
  const fallbackChartData = [
    { name: "", sales: 0, purchases: 0, bank: 0, cash: 0 },
  ];
  const chartSource = safeGraphData.length ? safeGraphData : fallbackChartData;
  const statsConfig = [
    {
      key: "total_sales",
      title: "total_sales",
      icon: <FaMoneyBillWave />,
    },
    {
      key: "total_purchases",
      title: "total_purchase",
      icon: <FaShoppingCart />,
    },
    {
      key: "bank_flow",
      title: "BankTransferFlow",
      icon: <FaUniversity />,
    },
    {
      key: "cash_flow",
      title: "CashInflow",
      icon: <FaWallet />,
    },
  ];

  const getDashboardStats = async (payload) => {
    const data = await fetchData("POST", "getStats", payload);
    if (data) setDashboardStats(data?.data);
  };

  const getGraphStats = async (payload) => {
    const data = await fetchData("POST", "getStatByMonths", payload);
    if (data) setGraphData(data?.data);
  };
  const fetchAllData = async (payload) => {
    setIsLoading(true);
    const apiCalls = [getDashboardStats(payload), getGraphStats(payload)];
    await Promise.all(apiCalls);
    setIsLoading(false);
  };
  useEffect(() => {
    const currentDate = new Date();
    const endDate = currentDate.toISOString().split("T")[0];

    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);
    const formattedStartDate = startDate.toISOString().split("T")[0];

    setFilterDate({
      start_date: formattedStartDate,
      end_date: endDate,
    });

    let payload = {
      start_date: formattedStartDate,
      end_date: endDate,
    };
    fetchAllData(payload);
  }, []);
  const handleUpdateFilters = async () => {
    let payload = {
      start_date: moment(filterDate.start_date).format("YYYY-MM-DD"),
      end_date: moment(filterDate.end_date).format("YYYY-MM-DD"),
    };
    await fetchAllData(payload);
    setSidebarVisible(false);
  };

  const salesVsPurchaseData = chartSource?.map((item) => ({
    name: item.month,
    sales: item.total_sales,
    purchases: item.total_purchases,
  }));

  const cashFlowData = chartSource?.map((item) => ({
    name: item.month,
    bank: item.bank_flow,
    cash: item.cash_flow,
  }));
  const tooltipStyle = {
    backgroundColor: "#fff",
    border: "none",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  };

  return (
    <div
      className={`min-h-screen xl:min-h-[calc(100vh-100px)] ${darkMode ? "bg-gray-900 text-white" : "transparent"}`}
    >
      <div className=" py-1">
        <div className="flex justify-end items-center mb-3">
          <FilterComponent
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            handleUpdateFilters={handleUpdateFilters}
            loading={isLoading}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {statsConfig.map((stat) => (
            <MetricCard
              key={stat.key}
              title={t(stat.key)}
              value={dashboardStats?.[stat.key] ?? 0}
              icon={stat.icon}
              currency={t("SAR")}
            />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title={t("sales_vs_purchase")}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={salesVsPurchaseData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey={t("sales")} fill="#50C878" radius={[4, 4, 0, 0]} />
                <Bar dataKey={t("purchases")} fill="#F4BB44" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title={t("bankflow_vs_cashflow")}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                <Bar dataKey={t("BankTransferFlow")} fill="#0096FF" radius={[4, 4, 0, 0]} />
                <Bar dataKey={t("CashInflow")} fill="#DE3163" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}
